import {
    initPerformanceMonitor,
    markAppMounted,
    markRouterReady,
    markSpaFullyReady
} from '@didi/fintech-performance';

import { getCookie } from './cookie';
import { trackEvent } from './omega';
import url from './url';

import { TechPerformanceId, TechPerformanceErrorType } from '@/constants/pub_event';

/**
 * 性能追踪统一管理工具
 * 封装 window.performanceTracker 相关方法，统一管理性能埋点
 */

interface MarkPopParams {
    popup_id: string;
    page_id?: string;
    page_version?: number | string;
}

interface InitTaskTrackerConfig {
    requiredTasks: string[];
    exec?: () => void;
}

interface UnifiedPerformanceState {
    initialized: boolean;
    p3Marked: boolean;
    p4Reported: boolean;
}

/**
 * 新性能 SDK 的页面级状态。
 *
 * marketing-svelte 是 MPA，每个页面只会加载一份 fmpTrack 模块，因此使用模块内
 * 状态即可，无需向 window 挂载额外字段。状态用于避免重复创建同名 performance
 * mark，尤其避免 markSpaFullyReady 被多次调用后重复发送 Omega 性能事件。
 */
const unifiedPerformanceState: UnifiedPerformanceState = {
    initialized: false,
    p3Marked: false,
    p4Reported: false
};

/** 统一性能 SDK 使用的自定义 Omega 事件名。 */
const UNIFIED_PERFORMANCE_EVENT_NAME = 'tech_performance_custom';

/** 从 pageInfo 注入的页面配置中取得 Omega page_name。 */
const getUnifiedPerformancePageName = (): string => {
    const pageConfig = window.__PAGE_CONFIG__;

    return pageConfig?.trackingPageId || pageConfig?.pageId || '';
};

const initUnifiedPerformanceMonitor = (): boolean => {
    try {
        // Omega 和页面名由页面公共配置提供；条件不满足时等待 P3 阶段再次尝试。
        if (
            typeof window === 'undefined' ||
            !window.Omega
        ) {
            return false;
        }

        if (unifiedPerformanceState.initialized) {
            return true;
        }

        const pageName = getUnifiedPerformancePageName();

        if (!pageName) {
            return false;
        }

        const params = {
            tracker: window.Omega,
            pageName,
            eventName: UNIFIED_PERFORMANCE_EVENT_NAME
        };

        console.log('[UnifiedPerformance][before]', {
            action: 'initPerformanceMonitor',
            params,
            performanceNow: performance.now()
        });
        initPerformanceMonitor(params);
        unifiedPerformanceState.initialized = true;

        return true;
    } catch (error) {
        // 新 SDK 异常不能阻断原有 performanceTracker 和页面业务逻辑。
        console.error('[UnifiedPerformance] init error:', error);

        return false;
    }
};

/** 获取旧性能 SDK 已维护的公共参数，获取失败时不阻断新性能上报。 */
const getUnifiedPerformanceContext = (): Record<string, unknown> => {
    try {
        return window.performanceTracker?.getCommonParams?.() || {};
    } catch (error) {
        console.error('[UnifiedPerformance] get context error:', error);

        return {};
    }
};

/**
 * 复用旧 reportP3 的调用时机，同时标记应用实例化和 MPA 框架初始化完成。
 * MPA 没有真实路由初始化过程，因此 appMounted 与 routerReady 使用同一时机。
 */
const markUnifiedPerformanceP3Ready = (): void => {
    try {
        if (!initUnifiedPerformanceMonitor()) {
            return;
        }

        if (unifiedPerformanceState.p3Marked) {
            return;
        }

        console.log('[UnifiedPerformance][before]', {
            action: 'markAppMounted + markRouterReady',
            pageName: getUnifiedPerformancePageName(),
            performanceNow: performance.now()
        });
        markAppMounted();
        markRouterReady();
        unifiedPerformanceState.p3Marked = true;
    } catch (error) {
        console.error('[UnifiedPerformance] mark P3 error:', error);
    }
};

/**
 * 复用旧 reportP4Direct 的调用时机，上报首屏关键内容已经可用。
 * markSpaFullyReady 会立即发送 Omega 事件，因此必须保证页面生命周期内只调用一次。
 */
const markUnifiedPerformanceP4Ready = (): void => {
    try {
        if (!initUnifiedPerformanceMonitor()) {
            return;
        }

        if (unifiedPerformanceState.p4Reported) {
            return;
        }

        // P3 缺失时无法得到可信的阶段耗时，直接跳过新 SDK 的 P4 上报。
        if (!unifiedPerformanceState.p3Marked) {
            return;
        }

        const context = getUnifiedPerformanceContext();

        console.log('[UnifiedPerformance][before]', {
            action: 'markSpaFullyReady',
            pageName: getUnifiedPerformancePageName(),
            context,
            performanceNow: performance.now()
        });
        // 先置位，避免 SDK 上报过程中的同步重入导致重复事件。
        unifiedPerformanceState.p4Reported = true;
        // SDK中忽略了 context，无法上报自定义埋点，但先这么实现
        markSpaFullyReady(context);
    } catch (error) {
        console.error('[UnifiedPerformance] mark P4 error:', error);
    }
};

const getErrorMsg = (error: unknown) => {
    let msg = '';

    try {
        msg = JSON.stringify(error);
    } catch {
        msg = String(error);
    }

    return msg;
};

/**
 * 上报 P3 性能指标
 */
export const reportP3 = (): void => {
    markUnifiedPerformanceP3Ready();

    try {
        if (window.performanceTracker) {
            window.performanceTracker.reportP3();
        } else {
            trackEvent(TechPerformanceId.TECH_PERFORMANCE_ERROR_TYPE, {
                error_type: TechPerformanceErrorType.p3_not_init,
            });
        }
    } catch (error) {
        // 避免埋点异常影响主流程
        console.error('reportP3 error:', error);
        trackEvent(TechPerformanceId.TECH_PERFORMANCE_ERROR_TYPE, {
            error_type: TechPerformanceErrorType.p3_catch_error,
            error_message: getErrorMsg(error),
        });
    }
};

/**
 * 上报 P4 性能指标（直接上报）
 */
export const reportP4Direct = (): void => {
    markUnifiedPerformanceP4Ready();

    try {
        if (window.performanceTracker) {
            window.performanceTracker.reportP4Direct();
        } else {
            trackEvent(TechPerformanceId.TECH_PERFORMANCE_ERROR_TYPE, {
                error_type: TechPerformanceErrorType.p4_direct_not_init,
            });
        }
    } catch (error) {
        console.error('reportP4Direct error:', error);
        trackEvent(TechPerformanceId.TECH_PERFORMANCE_ERROR_TYPE, {
            error_type: TechPerformanceErrorType.p4_direct_catch_error,
            error_message: getErrorMsg(error),
        });

    }
};

// fmpTrack 被页面入口导入时即初始化，尽量贴近应用 JS 加载完成时间。
initUnifiedPerformanceMonitor();

/**
 * 前置更新uid
 */
export const updateUid = (): void => {
    try {
        const uid = getCookie('uid') || url.get('uid') || '';

        updateCommonParams({ uid, pub_uid: uid });
    } catch (error) {
        console.error('updateuid error:', error);
    }
};

/**
 * 标记弹窗开始
 * @param params 弹窗参数
 */
export const markPopBegin = (params: MarkPopParams): void => {
    try {
        if (window.performanceTracker) {
            window.performanceTracker.markPopBegin({
                popup_id: params.popup_id,
            });
        }
    } catch (error) {
        console.error('markPopBegin error:', error);
    }
};

/**
 * 标记弹窗结束
 * @param params 弹窗参数
 */
export const markPopEnd = (params: MarkPopParams): void => {
    try {
        if (window.performanceTracker) {
            window.performanceTracker.markPopEnd({
                popup_id: params.popup_id,
            });
        }
    } catch (error) {
        console.error('markPopEnd error:', error);
    }
};

/**
 * 初始化任务追踪器
 * @param config 任务追踪器配置
 * @returns 任务追踪器实例
 */
export const initTaskTracker = (config: InitTaskTrackerConfig) => {
    try {
        if (window.performanceTracker) {
            window.taskPerformanceTasker = window.performanceTracker.taskTrackerTool.initTaskTracker({
                requiredTasks: config.requiredTasks,
                exec: () => {
                    reportP4Direct();
                    if (config.exec) {
                        config.exec();
                    }
                }
            });

            return window.taskPerformanceTasker;
        }

        return null;
    } catch (error) {
        console.error('initTaskTracker error:', error);

        return null;
    }
};

/**
 * 标记任务完成
 * @param taskName 任务名称
 */
export const markTaskCompleted = (taskName: string): void => {
    try {
        if (window.taskPerformanceTasker) {
            window.taskPerformanceTasker.markTaskCompleted(taskName);
        }
    } catch (error) {
        console.error('markTaskCompleted error:', error);
    }
};

/**
 * 更新 CommonParams，合并传入的 params 到当前公共参数
 * 业务侧可在登录后等场景调用，用于将 uid 等写入性能埋点公共参数
 * （UMD 下可能与 core/media 一致挂载在 window.performance 上，由打包统一挂载）
 */
export const updateCommonParams = (params: Record<string, unknown>): void => {
    try {
        if (window.performanceTracker?.updateCommonParams) {
            window.performanceTracker.updateCommonParams(params);
        }
    } catch (error) {
        console.error('updateCommonParams error:', error);
    }
};

/**
 * 上报页面停留时长
 */
export const pageLeave = (): void => {
    try {
        if (window.performanceTracker) {
            window.performanceTracker.pageLeave();
        }
    } catch (error) {
        console.error('pageLeave error:', error);
    }
};
